import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { trackInteraction } from "@/hooks/useInteractionTracking";
import { useNextTuesday, getNextTuesday } from "@/hooks/useNextTuesday";

const registrationSchema = z.object({
  name: z.string().trim().min(2, "נא להזין שם מלא").max(100),
  email: z.string().trim().email("נא להזין כתובת אימייל תקינה").max(255),
  phone: z.string().trim().optional(),
  website: z.string().optional(),
});

type FormData = z.infer<typeof registrationSchema>;
type FormErrors = Partial<Record<keyof FormData, string>>;
const pad = (n: number) => String(n).padStart(2, "0");

const SessionCountdownBanner = ({ dateLabel, daysUntil }: { dateLabel: string; daysUntil: number }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calc = () => {
      const target = getNextTuesday();
      const diff = target.getTime() - Date.now();
      if (diff <= 0) return;
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="bg-primary/10 border border-primary/20 rounded-xl px-4 py-4 mb-4 text-center">
      <p className="text-foreground text-sm font-bold mb-3">
        המפגש הקרוב: {dateLabel} • 20:30 • LIVE ב-Google Meet
      </p>
      <div className="flex items-center justify-center gap-2" dir="ltr">
        {[
          { value: timeLeft.days, label: "ימים" },
          { value: timeLeft.hours, label: "שעות" },
          { value: timeLeft.minutes, label: "דקות" },
          { value: timeLeft.seconds, label: "שניות" },
        ].map((unit, i) => (
          <div key={unit.label} className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              <span className="text-primary text-xl font-bold font-mono tabular-nums w-[2ch] text-center">
                {pad(unit.value)}
              </span>
              <span className="text-muted-foreground text-[9px]">{unit.label}</span>
            </div>
            {i < 3 && <span className="text-primary/40 text-lg font-light -mt-3">:</span>}
          </div>
        ))}
      </div>
      {daysUntil === 0 && (
        <p className="text-destructive text-xs font-bold mt-2 animate-pulse">
          🔴 המפגש היום בשעה 20:30!
        </p>
      )}
    </div>
  );
};

const LandingRegistrationSection = () => {
  const navigate = useNavigate();
  const { dateLabel, daysUntil } = useNextTuesday();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    website: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const saveLead = async (name: string, email: string, phone: string | null, method: string) => {
    const { data: leadId, error: dbError } = await supabase.rpc("insert_lead", {
      _name: name,
      _email: email,
      _phone: phone,
      _registration_method: method,
    });

    // Store leadId for interaction tracking
    if (leadId) {
      localStorage.setItem("leadId", leadId);
    }

    if (dbError) console.error("Registration failed");

    // Store session data for thank-you page display
    sessionStorage.setItem(
      "registeredLead",
      JSON.stringify({
        name,
        email,
        registeredAt: new Date().toISOString(),
      })
    );

    if (!dbError) {
      trackInteraction("form_submit", { method }, email);
      trackInteraction("session_register", undefined, email);

      // Send confirmation email (fire-and-forget)
      supabase.functions.invoke("send-registration-email", {
        body: { name, email },
      }).catch(() => {});
    }

    // Persist for thank-you page fallback
    localStorage.setItem("registeredLead", JSON.stringify({ name, email, registeredAt: new Date().toISOString() }));

    return !dbError;
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/thank-you",
      });
      if (error) {
        console.error("Sign-in failed");
        setIsGoogleLoading(false);
      }
      // On success, the user will be redirected
    } catch (err) {
      console.error("Sign-in failed");
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const validatedData = registrationSchema.parse(formData);
      // Honeypot check - bots fill hidden fields
      if (validatedData.website) {
        navigate("/thank-you");
        return;
      }
      await saveLead(validatedData.name, validatedData.email, validatedData.phone || null, "manual");
      navigate("/thank-you");
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as keyof FormData] = err.message;
          }
        });
        setErrors(fieldErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="register" className="px-6 py-16 md:py-24 bg-muted/50" dir="rtl">
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-foreground text-[28px] md:text-4xl font-bold text-center mb-3">
            הירשמו למפגש החינמי
          </h2>

          {/* Next session date banner with countdown */}
          <SessionCountdownBanner dateLabel={dateLabel} daysUntil={daysUntil} />

          <p className="text-muted-foreground text-center text-base mb-2">
            מאות אנשים כבר הפכו רעיונות לסקיצה ברורה.
          </p>
          <p className="text-foreground text-center text-sm font-bold mb-8">
            מפגש לייב אחד. הרבה ערך.
          </p>

          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-sm">

            {/* Google Sign-In Button */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className="w-full flex items-center justify-center gap-3 bg-background border border-border rounded-xl py-3 px-4 font-semibold text-foreground hover:bg-muted/60 transition-colors active:scale-[0.98] disabled:opacity-50 mb-5"
            >
              {isGoogleLoading ? (
                <span className="text-base">מתחבר...</span>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-base">הירשמו באמצעות Google</span>
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-muted-foreground text-sm">או הירשמו באופן ידני</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Manual Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot field - hidden from users, filled by bots */}
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <div className="flex flex-col gap-1.5">
                <label htmlFor="reg-name" className="text-foreground text-sm font-medium">שם מלא</label>
                <input
                  type="text"
                  id="reg-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full rounded-lg text-foreground focus:outline-0 focus:ring-2 focus:ring-primary border ${errors.name ? "border-destructive" : "border-border"} bg-background h-12 placeholder:text-muted-foreground px-4 text-base transition-all`}
                  placeholder="השם שלך"
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="reg-email" className="text-foreground text-sm font-medium">כתובת אימייל</label>
                <input
                  type="text"
                  id="reg-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  dir="ltr"
                  className={`w-full rounded-lg text-foreground focus:outline-0 focus:ring-2 focus:ring-primary border ${errors.email ? "border-destructive" : "border-border"} bg-background h-12 placeholder:text-muted-foreground px-4 text-base transition-all`}
                  placeholder="your@email.com"
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="reg-phone" className="text-foreground text-sm font-medium">
                  טלפון <span className="text-muted-foreground font-normal">(אופציונלי)</span>
                </label>
                <input
                  type="tel"
                  id="reg-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  dir="ltr"
                  className="w-full rounded-lg text-foreground focus:outline-0 focus:ring-2 focus:ring-primary border border-border bg-background h-12 placeholder:text-muted-foreground px-4 text-base transition-all"
                  placeholder="050-0000000"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-foreground text-background font-bold py-4 rounded-xl shadow-lg hover:opacity-90 transition-all active:scale-[0.98] disabled:opacity-50 text-lg mt-2"
              >
                {isSubmitting ? "שולח..." : "שריינו לי מקום במפגש"}
              </button>
            </form>

            <p className="text-muted-foreground text-xs text-center mt-4">
              המפגש חינמי – המקומות מוגבלים.
              <br />
              שריינו מקום עכשיו.
            </p>
            <a
              href="/offer"
              className="block text-center text-sm text-primary hover:underline mt-3 transition-colors"
            >
              כבר מכירים את השיטה? עברו ישר לקורסים ←
            </a>
          </div>

          {/* Live event banner */}
          <div className="text-center mt-6 space-y-1">
            <p className="text-muted-foreground text-sm flex items-center justify-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" />
              <span>LIVE • {dateLabel} • 20:30 • חינמי לחלוטין</span>
            </p>
            <p className="text-muted-foreground/60 text-xs">
              נשארו מקומות אחרונים למפגש הקרוב
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LandingRegistrationSection;
